<script lang="ts">
export default {
  name: 'KkGoogleMapPostPreview',
}
</script>
<script setup lang="ts">
import { computed, ref } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import moment from 'moment'
import type { Post } from '../../types'
import { KkPostPreviewLayout } from '../../../../domains/post'

const store = useStore()
const { t } = useI18n()

const post = computed<Post>(() => store.getters['companiesStorePosts/post'])
const actionTypes = computed(() => store.getters['companiesStorePosts/actionTypes'])
const showEventType = computed<boolean>(() => store.getters['companiesStorePosts/showEventType'])
const showCallToActionType = computed<boolean>(() => store.getters['companiesStorePosts/showCallToActionType'])
const showOfferType = computed<boolean>(() => store.getters['companiesStorePosts/showOfferType'])
const eventTime = computed<string>(() => store.state.companiesStorePosts.eventTime)
const eventStartedTime = computed<string>(() => store.state.companiesStorePosts.eventStartedTime)
const eventEndedTime = computed<string>(() => store.state.companiesStorePosts.eventEndedTime)

const hasContent = computed<boolean>(() => {
  return Boolean(
    post.value.postImages.length ||
    post.value.title ||
    post.value.event.title ||
    dateTime.value ||
    post.value.summary ||
    showOfferType.value ||
    post.value.offer.redeemOnlineUrl ||
    post.value.offer.couponCode ||
    post.value.offer.termsConditions ||
    (showCallToActionType.value && post.value.callToAction.actionType !== 'ACTION_TYPE_UNSPECIFIED'))
})

const dateTime = computed<string>(() => {
  if (post.value.topicType === 'EVENT' || post.value.topicType === 'OFFER') {
    const startedAt = moment(post.value.event.startedAt).format(t('format.date.excludeYear'))
    const endedAt = moment(post.value.event.endedAt).format(t('format.date.excludeYear'))
    let startedTime = eventStartedTime.value === '00:00' && eventEndedTime.value === '23:59' ? '' : ', ' + eventStartedTime.value
    let endedTime = eventStartedTime.value === '00:00' && eventEndedTime.value === '23:59' ? '' : ', ' + eventEndedTime.value

    return startedAt + startedTime + ' - ' + endedAt + endedTime
  }

  return ''
})

const couponCodeDateTime = computed<string>(() => {
  if (post.value.topicType === 'OFFER') {
    const startedAt = moment(post.value.event.startedAt).format(t('format.date.default'))
    const endedAt = moment(post.value.event.endedAt).format(t('format.date.default'))
    const startedTime = eventTime.value && eventStartedTime.value ? ' ' + eventStartedTime.value : ''
    const endedTime = eventTime.value && eventEndedTime.value ? ' ' + eventEndedTime.value : ''

    return startedAt + startedTime + ' - ' + endedAt + endedTime
  }

  return ''
})

const postTitle = computed<string>(() => {
  if (post.value.topicType === 'STANDARD') {
    return post.value.title ?? ''
  } else {
    return post.value.event.title ?? ''
  }
})

const summaryText = computed<string>(() => {
  if (post.value.topicType === 'STANDARD' && Boolean(postTitle.value)) {
    return `【${postTitle.value}】${post.value.summary ?? ''}`
  } else {
    return `${post.value.summary ?? ''}`
  }
})

const termsConditionsToggle = ref<boolean>(false)
</script>

<template>
  <KkPostPreviewLayout service="google_map" :post="post" :has-content="hasContent">
    <!-- 写真 -->
    <div v-if="post.postImages.length" class="preview__imageWrapper">
      <img :src="post.postImages[0].url" :alt="`${post.postImages[0].id}`" class="preview__image">
    </div>

    <!-- タイトル (イベント、特典のみ) -->
    <p v-show="post.event.title && showEventType" class="preview__title">
      {{ post.event.title }}
    </p>

    <!-- 開始終了 (イベント、特典のみ) -->
    <p v-show="dateTime && showEventType" class="preview__dateTime u-fsS">
      {{ dateTime }}
    </p>

    <p v-show="summaryText" class="preview__text">{{ summaryText }}</p>

    <!-- 特典情報 (特典のみ) -->
    <div v-show="showOfferType">
      <div v-show="post.offer.redeemOnlineUrl" class="preview__link">
        {{ $t('pages.CompaniesStorePost.useOfferOnline') }}
      </div>

      <div v-show="post.offer.couponCode" class="preview__couponCode">
        <p>{{ $t('pages.CompaniesStorePost.showCodeAtStore') }}</p>
        <span>{{ post.offer.couponCode }}</span>
        <p>{{ $t('labels.valid') }}: {{ couponCodeDateTime }}</p>
      </div>

      <div v-show="post.offer.termsConditions" class="preview__termsConditions">
        <button class="c-select" :class="{ show: termsConditionsToggle }"
                @click="termsConditionsToggle = !termsConditionsToggle"
        >
          <span>{{ $t('labels.terms') }}</span>
        </button>
        <p v-show="termsConditionsToggle">{{ post.offer.termsConditions }}</p>
      </div>
    </div>

    <!-- ボタン -->
    <div v-show="showCallToActionType && post.callToAction.actionType !== 'ACTION_TYPE_UNSPECIFIED'"
         class="preview__button"
    >
      {{ actionTypes[post.callToAction.actionType] }}
    </div>
  </KkPostPreviewLayout>
</template>

<style lang="scss" scoped>
.preview {
  &__image {
    margin: 2px;
    width: 100%;
  }

  &__title {
    font-size: 18px;
    padding: 12px 12px 0;
    word-break: break-all;
  }

  &__dateTime {
    color: #8A8D90;
    margin: 0 12px;
  }

  &__text {
    padding: 12px 12px  0;
    white-space: pre-wrap;
    word-break: break-all;
  }

  &__link {
    cursor: pointer;
    margin: 8px 12px 0;
    color: #1A73E8;
    font-size: 13px;
    font-weight: bold;
    text-decoration: none;
  }

  &__couponCode {
    margin: 12px 12px;
    border: 2px dashed #DADCE0;
    border-radius: 6px;
    background-color: #F8F9FA;
    text-align: center;

    p {
      margin: 12px 16px;
      color: #8A8D90;
      font-size: 12px;
    }

    span {
      color: #000000;
      font-size: 20px;
      font-weight: bold;
      word-break: break-all;
    }
  }

  &__termsConditions {
    margin: 8px 12px 0;

    .c-select {
      display: block;
      text-align: left;
      width: 100%;
      color: #8A8D90;
      font-size: 13px;

      &.show {
        &::before {
          transform: translateY(-50%) rotate(0deg);
        }
      }
    }

    p {
      color: #8A8D90;
      font-size: 13px;
      word-break: break-all;
    }
  }

  &__button {
    display: inline-block;
    margin: 8px 12px 0;
    padding: 8px 24px;
    color: #ffffff;
    background-color: #1A73E8;
    border-radius: 4px;
    text-decoration: none;
  }
}
</style>
